import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Check, ChevronsUpDown, FileText, Gift, Link2, Lock, Plus, Printer, Save, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ImageUploader, PageHeader, Panel, StatusBadge, Tone } from "@/components/shared";
import { ShareDialog } from "@/components/ShareDialog";
import { PricingRulesSheet } from "@/components/pricing/PricingRules";
import { AssignClientDialog } from "@/components/AssignClientDialog";
import { MoneyInput, QuotePricingSheet, TargetMarginInput, priceForMargin } from "@/components/QuotePricing";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/roles";
import { api, CATEGORIES, errMsg, inr, label, useApi } from "@/lib/api";

const FIXED = CATEGORIES.slice(0, 15);
const AMC_LABELS = { "1Y": "GN PRIVILEGE PACKAGE 1 YEAR", "2Y": "GN PRIVILEGE PACKAGE 2 YEARS", "3Y": "GN PRIVILEGE PACKAGE 3 YEARS" };
const DEFAULT_RATES = { "1Y": 10, "2Y": 12, "3Y": 14 };
const inr2 = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const blank = (category) => ({ key: Math.random().toString(36).slice(2), category, productId: null, customTitle: "", isUnlisted: false, procurementCost: 0, inputValue: 0, taxableValue: 0, gstPercent: 18 });
export const belowCost = (r) => !r.isUnlisted && !!r.productId && r.inputValue < r.procurementCost - 0.005;

function ProductPicker({ products, row, onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const opts = products.filter((p) => p.category === row.category);
  const sel = products.find((p) => p.id === row.productId);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled} className="w-full justify-between font-normal h-9" data-testid={`product-picker-${row.category}`}>
          <span className="truncate">{sel ? sel.name : <span className="text-muted-foreground">Search {label(row.category)}…</span>}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Type to search…" data-testid={`product-search-${row.category}`} />
          <CommandList>
            <CommandEmpty>No products in this category.</CommandEmpty>
            {opts.map((p) => (
              <CommandItem key={p.id} value={`${p.name} ${p.brand} ${p.sku}`} onSelect={() => { onPick(p); setOpen(false); }} data-testid={`product-option-${p.sku}`}>
                <Check className={`h-3.5 w-3.5 mr-2 ${row.productId === p.id ? "opacity-100" : "opacity-0"}`} />
                <div className="flex-1 min-w-0"><div className="truncate text-sm">{p.name}</div><div className="text-[11px] text-muted-foreground">{p.brand} · FIFO {inr(p.fifoCost ?? p.purchasePrice)} · {p.stockQuantity} in stock</div></div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Row({ row, products, update, remove, lowMargin, locked, target }) {
  const selling = row.taxableValue * (1 + row.gstPercent / 100);
  const profit = row.taxableValue - row.inputValue;
  const active = row.productId || row.isUnlisted;
  const below = belowCost(row);
  return (
    <tr className="border-b last:border-0 align-middle" data-testid={`quote-row-${row.category}`}>
      <td className="px-3 py-2 text-xs font-medium whitespace-nowrap w-36">{label(row.category)}</td>
      <td className="px-3 py-2 min-w-[240px] max-w-[320px]">
        {row.isUnlisted ? (
          <Input value={row.customTitle} disabled={locked} placeholder="Custom part title" onChange={(e) => update({ customTitle: e.target.value })} className="h-9" data-testid={`unlisted-title-${row.category}`} />
        ) : (
          <ProductPicker products={products} row={row} disabled={locked}
            onPick={(p) => { const c = p.fifoCost ?? p.purchasePrice; update({ productId: p.id, procurementCost: c, inputValue: c, gstPercent: 18, taxableValue: priceForMargin(c, target) }); }} />
        )}
      </td>
      <td className="px-3 py-2 text-center">
        <Switch checked={row.isUnlisted} disabled={locked} onCheckedChange={(v) => update({ isUnlisted: v, productId: null, customTitle: "", procurementCost: 0, inputValue: 0, taxableValue: 0 })} data-testid={`unlisted-toggle-${row.category}`} />
      </td>
      <td className="px-3 py-2 w-40">
        <MoneyInput disabled={!active || locked} value={row.inputValue} className={`h-9 num w-28 ${below ? "border-rose-500 focus-visible:ring-rose-500" : ""}`} data-testid={`input-value-${row.category}`}
          onValue={(v) => update({ ...(row.isUnlisted ? { procurementCost: v } : {}), inputValue: v, taxableValue: priceForMargin(v, target) })} />
        {below && <p className="text-[11px] leading-tight text-rose-600 mt-1 w-36" data-testid={`input-value-error-${row.category}`}>Input value cannot be lower than the purchase cost of {inr(row.procurementCost)}</p>}
        {active && !row.isUnlisted && !below && <p className="text-[10px] text-muted-foreground mt-0.5 num" data-testid={`purchase-cost-${row.category}`}>Cost {inr(row.procurementCost)}</p>}
      </td>
      <td className="px-3 py-2 w-36">
        <MoneyInput disabled={!active || locked} value={row.taxableValue} onValue={(v) => update({ taxableValue: v })}
          className={`h-9 num w-28 ${lowMargin && active ? "border-rose-500 focus-visible:ring-rose-500" : ""}`} data-testid={`taxable-input-${row.category}`} />
      </td>
      <td className="px-3 py-2 w-36"><MoneyInput disabled={!active || locked} value={selling} className="h-9 num w-28" data-testid={`selling-input-${row.category}`}
        onValue={(v) => update({ taxableValue: Math.round((v / (1 + row.gstPercent / 100)) * 100) / 100 })} /></td>
      <td className={`px-3 py-2 num text-sm whitespace-nowrap ${profit < 0 ? "text-rose-600" : "text-emerald-600 dark:text-emerald-400"}`}>{active ? inr(profit) : "—"}</td>
      <td className="px-2 py-2 w-10">
        {!locked && (active || remove) && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={remove || (() => update({ ...blank(row.category), key: row.key }))} data-testid={`clear-row-${row.category}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
      </td>
    </tr>
  );
}

export default function QuoteBuilder() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const qid = sp.get("quote");
  const nav = useNavigate();
  const { data: lead, reload: reloadLead } = useApi(id ? `/leads/${id}` : null);
  const { data: products } = useApi("/inventory");
  const { data: quote, reload } = useApi(qid ? `/quotations/${qid}` : null);
  const { data: pricing, reload: reloadPricing } = useApi("/settings/pricing");
  const { user } = useAuth();
  const [amcPlan, setAmcPlan] = useState("none");
  const TIERS = (pricing?.tiers || []).map((x) => ({ at: x.minProfit, gifts: x.giftName }));
  const [rules, setRules] = useState(null);
  const [assign, setAssign] = useState(false);
  const [noGifts, setNoGifts] = useState(null);
  const disableGifts = noGifts ?? !!quote?.disableGifts;
  const globals = { defaultMarginPct: pricing?.defaultMarginPct ?? 12, minMarginPct: pricing?.minMarginPct ?? 5 };
  const qr = rules || { targetMarginPct: quote?.targetMarginPct ?? globals.defaultMarginPct, minMarginPct: quote?.minMarginPct ?? globals.minMarginPct };
  const minMargin = qr.minMarginPct === "" ? globals.minMarginPct : +qr.minMarginPct;
  const target = qr.targetMarginPct === "" ? globals.defaultMarginPct : +qr.targetMarginPct;
  const rulesInvalid = !(minMargin >= 0 && minMargin < 95 && target >= 0 && target < 95);
  const onRules = (r) => {
    setRules(r);
    const m = +r.targetMarginPct;
    if (r.targetMarginPct !== "" && m >= 0 && m < 95 && m !== target)
      setRows((rs) => rs.map((x) => (x.inputValue > 0 ? { ...x, taxableValue: priceForMargin(x.inputValue, m) } : x)));
  };
  const [rows, setRows] = useState(FIXED.map(blank));
  const [images, setImages] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [share, setShare] = useState(false);

  useEffect(() => {
    if (!quote) return;
    const fixed = FIXED.map(blank);
    const extra = [];
    quote.items.forEach((it) => {
      const r = { ...blank(it.category), ...it, inputValue: it.inputValue ?? it.procurementCost, key: it.id };
      const slot = fixed.findIndex((x) => x.category === it.category && !x.productId && !x.isUnlisted);
      if (it.category !== "CUSTOM_ACCESSORY" && slot >= 0) fixed[slot] = r; else extra.push(r);
    });
    setRows([...fixed, ...extra]);
    setImages(quote.referenceImages || []);
    setNotes(quote.internalNotes || "");
    setAmcPlan(quote.amcPlan || "none");
    setRules(null);
    setNoGifts(null);
  }, [quote]);

  const locked = quote && ["APPROVED_BY_CLIENT", "SUPERSEDED"].includes(quote.status);
  const filled = rows.filter((r) => r.productId || (r.isUnlisted && r.customTitle));
  const rates = pricing?.amcRates || DEFAULT_RATES;
  const t = useMemo(() => {
    const proc = filled.reduce((s, r) => s + r.inputValue, 0);
    const taxable = filled.reduce((s, r) => s + r.taxableValue, 0);
    const gst = filled.reduce((s, r) => s + (r.taxableValue * r.gstPercent) / 100, 0);
    const profit = taxable - proc;
    const amc = (taxable * (rates[amcPlan] || 0)) / 100;
    return { proc, taxable, gst, hardware: taxable + gst, amc, amcGst: amc * 0.18, grand: taxable + gst + amc * 1.18, profit, margin: proc ? (profit / proc) * 100 : 0 };
  }, [filled, amcPlan, rates]);
  const lowMargin = filled.length > 0 && t.margin < minMargin;
  const tierIdx = TIERS.filter((x) => t.profit > x.at).length;
  const next = TIERS[tierIdx];
  const prevAt = tierIdx ? TIERS[tierIdx - 1].at : 0;
  const pct = next ? Math.max(0, Math.min(100, ((t.profit - prevAt) / (next.at - prevAt)) * 100)) : 100;
  const addMore = next ? Math.max(0, Math.round((next.at - t.profit + 1) * 1.18)) : 0;

  const update = (key) => (patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const save = async () => {
    setSaving(true);
    const body = { items: filled.map(({ key, ...r }) => ({ ...r, id: quote ? r.id : null })), referenceImages: images, internalNotes: notes, amcPlan: amcPlan === "none" ? null : amcPlan,
      disableGifts, targetMarginPct: target, ...(can(user, "pricing:manage") ? { minMarginPct: minMargin } : {}) };
    try {
      if (quote) { await api.put(`/quotations/${quote.id}`, body); toast.success("Quotation saved"); reload(); }
      else {
        const r = await api.post(id ? `/leads/${id}/quotations` : "/quotations", body);
        toast.success(`Quotation ${r.data.quoteNo} created`);
        if (id) reloadLead();
        nav(id ? `/leads/${id}/quote-builder?quote=${r.data.id}` : `/quotes/builder?quote=${r.data.id}`, { replace: true });
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  if ((id && !lead) || !products || (qid && !quote)) return null;
  const assigned = quote ? !!quote.leadId : !!id;
  const badInput = filled.some(belowCost);
  const saveDisabled = locked || lowMargin || rulesInvalid || badInput || !filled.length || saving;

  return (
    <TooltipProvider>
      <Link to={lead ? `/leads/${id}` : "/quotes"} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-lead"><ArrowLeft className="h-4 w-4 mr-1" />{lead ? lead.lead.clientName : "Quotations"}</Link>
      <PageHeader title={quote ? `Quotation ${quote.quoteNo || quote.quoteCode} · Rev-${quote.versionNumber}` : lead ? "New quotation" : "New quick quote"}
        subtitle={lead ? `${lead.lead.leadNumber}${lead.lead.budget ? ` · Budget ${inr(lead.lead.budget)}` : ""} · Owner: ${lead.assignedSalesName || "Unassigned (auto-assigned on first quote)"}` : assigned ? "Assigned to a client" : "Unassigned quick quote · assign a client to share it or convert it to a project"}>
        {quote && <StatusBadge status={quote.status} data-testid="builder-quote-status" />}
        {locked && <Tone tone="gray"><Lock className="h-3 w-3 mr-1" />Locked — clone to edit</Tone>}
        <TargetMarginInput value={qr.targetMarginPct} locked={locked} invalid={rulesInvalid} onChange={(v) => onRules({ ...qr, targetMarginPct: v })} />
        {can(user, "pricing:manage") && <PricingRulesSheet onSaved={reloadPricing} />}
        {can(user, "pricing:manage") && <QuotePricingSheet rules={qr} globals={globals} onChange={onRules} canEdit locked={locked} />}
        {lead?.project && can(user, "projects:view") && <Button variant="outline" onClick={() => nav(`/projects/${lead.project.id}/order-confirmation`)} data-testid="quote-order-confirmation-button"><FileText className="h-4 w-4 mr-2" />Order Confirmation</Button>}
        {quote && !quote.leadId && <Button variant="outline" onClick={() => setAssign(true)} data-testid="assign-client-button"><UserPlus className="h-4 w-4 mr-2" />Assign to Client / Lead</Button>}
        {quote && <Button className="bg-[#d32f2f] hover:bg-[#b71c1c] text-white" onClick={() => nav(`/quotes/${quote.id}/document?print=1`)} data-testid="print-quote-button"><Printer className="h-4 w-4 mr-2" />Print / Download PDF</Button>}
        {quote && quote.leadId && quote.status !== "SUPERSEDED" && <Button variant="outline" onClick={() => setShare(true)} data-testid="generate-share-link-button"><Link2 className="h-4 w-4 mr-2" />Generate Share Link</Button>}
        <Tooltip>
          <TooltipTrigger asChild><span><Button onClick={save} disabled={saveDisabled} data-testid="save-quote-button"><Save className="h-4 w-4 mr-2" />Save quotation</Button></span></TooltipTrigger>
          {lowMargin && <TooltipContent>Quote value is too low</TooltipContent>}
        </Tooltip>
      </PageHeader>
      {lowMargin && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-400" data-testid="margin-guard-banner">
          <AlertCircle className="h-4 w-4" />Quote value is too low. Raise the Target Profit Margin % or the line prices to save this quotation.
        </div>
      )}
      {rulesInvalid && <div className="mb-4 text-sm text-rose-600" data-testid="target-margin-error">Enter a target margin from 0 to 94.99%.</div>}
      {badInput && <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-400" data-testid="input-value-guard-banner">
        <AlertCircle className="h-4 w-4" />One or more input values are below the item's purchase cost. Fix them to save.</div>}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3 space-y-6 min-w-0">
          <Panel title="Build configuration · Internal team view" action={!locked && <Button size="sm" variant="ghost" onClick={() => setRows([...rows, { ...blank("CUSTOM_ACCESSORY"), isUnlisted: true }])} data-testid="add-accessory-row"><Plus className="h-4 w-4 mr-1" />Add Unlisted Item</Button>}>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="quote-builder-table">
                <thead><tr className="border-b bg-muted/40 text-[11px] text-muted-foreground">
                  {["Category", "Product", "Unlisted", "Input Value", "Taxable value", "Selling price (incl. GST)", "Profit", ""].map((h) => <th key={h} className="text-left font-medium px-3 py-2">{h}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => <Row key={r.key} row={r} products={products} update={update(r.key)} lowMargin={lowMargin} locked={locked} target={target}
                    remove={i >= FIXED.length ? () => setRows(rows.filter((x) => x.key !== r.key)) : null} />)}
                </tbody>
              </table>
            </div>
          </Panel>
          <div className="grid md:grid-cols-2 gap-6">
            <Panel title="Customer reference photos"><div className="p-5"><ImageUploader images={images} onChange={setImages} testid="reference-images" disabled={locked} /></div></Panel>
            <Panel title="Internal notes"><div className="p-5"><Textarea rows={4} value={notes} disabled={locked} onChange={(e) => setNotes(e.target.value)} placeholder="Visible to team only" data-testid="internal-notes-input" /></div></Panel>
          </div>
        </div>
        <div className="space-y-4 xl:sticky xl:top-6 self-start min-w-0">
          <Panel title="GN Privilege Package (AMC)">
            <div className="p-5 space-y-2">
              <Select value={amcPlan} onValueChange={setAmcPlan} disabled={locked}>
                <SelectTrigger data-testid="amc-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No package</SelectItem>
                  {Object.entries(AMC_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.replace("GN PRIVILEGE PACKAGE ", "GN Privilege · ")} ({rates[k]}% + GST)</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Charged on total hardware taxable value; GST 18% applied on the package.</p>
            </div>
          </Panel>
          <Panel title="Summary">
            <div className="p-5 space-y-2.5 text-sm" data-testid="quote-summary">
              {[["Input value", t.proc, "sum-input-value"], ["Taxable value", t.taxable, "sum-taxable"], ["GST", t.gst, "sum-gst"]].map(([l, v, tid]) => (
                <div key={l} className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="num" data-testid={tid}>{inr2(v)}</span></div>
              ))}
              {t.amc > 0 && <>
                <div className="flex justify-between border-t pt-2.5"><span className="text-muted-foreground">Hardware total</span><span className="num" data-testid="sum-hardware-total">{inr2(t.hardware)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Privilege Package ({rates[amcPlan]}%)</span><span className="num" data-testid="sum-amc">{inr2(t.amc)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Package GST (18%)</span><span className="num" data-testid="sum-amc-gst">{inr2(t.amcGst)}</span></div>
              </>}
              <div className="flex justify-between border-t pt-2.5 font-semibold"><span>Grand total</span><span className="num" data-testid="sum-grand-total">{inr2(t.grand)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Profit</span><span className={`num font-semibold ${t.profit < 0 ? "text-rose-600" : "text-emerald-600 dark:text-emerald-400"}`} data-testid="sum-net-profit">{inr2(t.profit)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Margin on input value</span><span className={`num ${lowMargin ? "text-rose-600" : ""}`} data-testid="sum-margin">{t.margin.toFixed(2)}%</span></div>
            </div>
          </Panel>
          <Panel title="Free gift milestones" action={<label className="flex items-center gap-2 text-xs">Disable Free Gift
            <Switch checked={disableGifts} disabled={locked} onCheckedChange={setNoGifts} data-testid="disable-gift-toggle" /></label>}>
            {disableGifts ? <div className="p-5 text-xs text-muted-foreground" data-testid="gifts-disabled-note">Free gifts are switched off for this quotation; no gift row appears in the builder, preview or PDF.</div> :
            <div className="p-5 space-y-4" data-testid="gift-milestones">
              <div className="space-y-2">
                {TIERS.map((x, i) => (
                  <div key={x.at} className={`flex items-center gap-2 text-xs rounded-md border px-2.5 py-2 ${i < tierIdx ? "border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300" : "text-muted-foreground"}`} data-testid={`gift-tier-${i + 1}`}>
                    {i < tierIdx ? <Check className="h-3.5 w-3.5" /> : <Gift className="h-3.5 w-3.5" />}<span className="font-medium">Tier {i + 1}</span><span className="ml-auto">{x.gifts}</span>
                  </div>
                ))}
              </div>
              <Progress value={pct} className="h-1.5" />
              <div className="text-xs" data-testid="next-gift-message">
                {next ? <>Add <span className="num font-semibold">{inr(addMore)}</span> more to cart value to unlock next gift tier</> : "All gift tiers unlocked"}
              </div>
            </div>}
          </Panel>
          {lead?.lead.requirements && <Panel title="Client brief"><div className="p-5 text-sm text-muted-foreground leading-relaxed">{lead.lead.requirements}</div></Panel>}
          {quote && <Label className="text-[11px] text-muted-foreground block">Save changes before sharing or printing so the document shows the latest pricing.</Label>}
        </div>
      </div>
      {assign && quote && <AssignClientDialog quote={quote} open onClose={() => setAssign(false)}
        onAssigned={(q) => { setAssign(false); nav(`/leads/${q.leadId}/quote-builder?quote=${q.id}`, { replace: true }); reload(); }} />}
      {share && quote && <ShareDialog quote={quote} open onClose={() => setShare(false)} onShared={reload} />}
    </TooltipProvider>
  );
}
